import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  TWDataTable,
  TWSelect,
  TWButton,
  EmptyState,
  ToggleSwitch,
} from '../../../components/ui';
import { headerCls } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';
import ReferralStatusBadge from '../../referral/components/ReferralStatusBadge';
import { parseFraudFlags, formatFraudFlag } from './fraudFlags';

dayjs.extend(utc);

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '0', label: 'Pending' },
  { value: '5', label: 'Under review' },
  { value: '10', label: 'Active' },
  { value: '20', label: 'Expired' },
  { value: '30', label: 'Revoked' },
  { value: '40', label: 'Rejected' },
];

// Pending(0)/PendingReview(5) -> approve+reject; Active(10) -> revoke; diğer -> yok.
const actionsForStatus = (status) => {
  if (status === 0 || status === 5) return ['approve', 'reject'];
  if (status === 10) return ['revoke'];
  return [];
};

// Aksiyon hücresine tıklama satır tıklamasını (detay modalı) tetiklemesin.
const stopRowClick = (e) => e.stopPropagation();

const buildColumns = (onAction) => [
  {
    title: <span className={headerCls}>Referrer</span>,
    key: 'referrer',
    render: (_, row) => (
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {row.referrercompanyname || '—'}
      </span>
    ),
  },
  {
    title: <span className={headerCls}>Referred</span>,
    key: 'referred',
    render: (_, row) => (
      <span className="text-sm text-slate-700 dark:text-slate-300">
        {row.referredcompanyname || '—'}
      </span>
    ),
  },
  {
    title: <span className={headerCls}>Amount</span>,
    key: 'amount',
    render: (_, row) => (
      <div>
        <div className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {formatCurrency(row.rewardamount)}
        </div>
        <div className="text-xs tabular-nums text-slate-400 dark:text-slate-500">
          bal {formatCurrency(row.currentbalance)}
        </div>
      </div>
    ),
  },
  {
    title: <span className={headerCls}>Status</span>,
    key: 'status',
    render: (_, row) => (
      <ReferralStatusBadge
        kind="status"
        code={row.status}
        fallbackLabel={row.statusname}
      />
    ),
  },
  {
    title: <span className={headerCls}>Fraud</span>,
    key: 'fraud',
    render: (_, row) => {
      const flags = parseFraudFlags(row.fraudflags);
      if (flags.length === 0) {
        return <span className="text-slate-300 dark:text-slate-600">—</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {flags.map((flag) => (
            <span
              key={flag}
              className="rounded bg-rose-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
            >
              {formatFraudFlag(flag)}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    title: <span className={headerCls}>Created</span>,
    key: 'created',
    render: (_, row) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">
        {row.createdate ? dayjs.utc(row.createdate).format('MMM D, YYYY') : '—'}
      </span>
    ),
  },
  {
    title: <span className={headerCls}>Actions</span>,
    key: 'actions',
    render: (_, row) => {
      const actions = actionsForStatus(row.status);
      if (actions.length === 0) {
        return <span className="text-slate-300 dark:text-slate-600">—</span>;
      }
      return (
        <div className="flex gap-2" onClick={stopRowClick}>
          {actions.includes('approve') ? (
            <TWButton
              size="sm"
              variant="primary"
              label="Approve"
              onClick={() => onAction('approve', row)}
            />
          ) : null}
          {actions.includes('reject') ? (
            <TWButton
              size="sm"
              variant="default"
              label="Reject"
              onClick={() => onAction('reject', row)}
            />
          ) : null}
          {actions.includes('revoke') ? (
            <TWButton
              size="sm"
              variant="danger"
              label="Revoke"
              onClick={() => onAction('revoke', row)}
            />
          ) : null}
        </div>
      );
    },
  },
];

const ReferralRewardsTable = ({
  data,
  loading,
  pagination,
  onPageChange,
  filter,
  onFilterChange,
  onAction,
  onRowClick,
  companyOptions,
}) => {
  const isEmpty = !loading && (!data || data.length === 0);
  const hasFilter =
    filter.status !== '' || filter.fraudonly || !!filter.companyid;
  const columns = buildColumns(onAction);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-52">
          <TWSelect
            value={filter.status}
            onChange={(value) => onFilterChange({ ...filter, status: value })}
            allowClear={false}
            aria-label="Filter rewards by status"
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="w-full sm:w-64">
          <TWSelect
            value={filter.companyid || undefined}
            onChange={(value) =>
              onFilterChange({ ...filter, companyid: value || '' })
            }
            allowClear
            placeholder="All companies"
            aria-label="Filter rewards by company"
            options={companyOptions}
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <ToggleSwitch
            checked={filter.fraudonly}
            tone="rose"
            onChange={(v) => onFilterChange({ ...filter, fraudonly: v })}
          />
          Fraud only
        </label>
      </div>

      {isEmpty ? (
        <EmptyState
          variant="dashed"
          title="No referral rewards"
          description={
            hasFilter
              ? 'No rewards match these filters.'
              : 'Referral rewards will appear here as restaurants invite each other.'
          }
        />
      ) : (
        <TWDataTable
          columns={columns}
          data={data}
          rowKey="id"
          loading={loading}
          onRowClick={onRowClick}
          pagination={
            pagination && pagination.totalRecords > pagination.pageSize
              ? {
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  total: pagination.totalRecords,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `Showing ${range[0]} to ${range[1]} of ${total} rewards`,
                  onChange: onPageChange,
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default ReferralRewardsTable;
